// ============================================
// 📦 필요한 라이브러리 가져오기
// ============================================
import React, { useState, useEffect } from 'react';
import { auth, analytics, logEvent } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';

// Toast Provider 추가
import { ToastProvider } from './components/Toast';

// 각 페이지 컴포넌트들 가져오기
import Onboarding from './components/Onboarding';
import Login from './components/Login';
import Profile from './components/Profile';
import StairRecord from './components/StairRecord';
import Home from './components/Home';
import Ranking from './components/Ranking';
import Friends from './components/Friends';
import Groups from './components/Groups';
import Feedback from './components/Feedback';
import AdminPanel from './components/AdminPanel';
import Tutorial from './components/Tutorial';
import './App.css';

// ============================================
// 🔐 관리자 이메일 목록
// ============================================
const ADMIN_EMAILS = [
  'moveoftoday.info@gmail.com',
];

// ============================================
// 🏠 메인 앱 컴포넌트
// ============================================
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('home');
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  useEffect(() => {
    // 온보딩 완료 여부 확인
    const onboardingDone = localStorage.getItem('onboardingComplete') === 'true';
    setOnboardingComplete(onboardingDone);

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      // 신규 사용자 체크
      if (currentUser) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists() || !userDoc.data().nickname || !userDoc.data().region) {
          setIsNewUser(true);
          setCurrentPage('profile');
        }

        // 로그인 이벤트 추적
        logEvent(analytics, 'login', {
          method: 'firebase_auth'
        });
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 페이지 전환 추적
  useEffect(() => {
    if (user && currentPage) {
      logEvent(analytics, 'page_view', {
        page_title: currentPage,
        page_location: window.location.href,
        page_path: `/${currentPage}`
      });
    }
  }, [currentPage, user]);

  const handleProfileComplete = () => {
    setIsNewUser(false);
    setCurrentPage('home');
  };

  const handleLogout = async () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      try {
        // 로그아웃 이벤트 추적
        logEvent(analytics, 'logout');

        await signOut(auth);
        setCurrentPage('home');
      } catch (error) {
        console.error('로그아웃 실패:', error);
        alert('로그아웃에 실패했습니다.');
      }
    }
  };

  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingText}>로딩 중...</div>
      </div>
    );
  }

  if (!user) {
    // 온보딩을 완료하지 않은 경우 온보딩 페이지 표시
    if (!onboardingComplete) {
      return <Onboarding onComplete={() => setOnboardingComplete(true)} />;
    }
    // 온보딩 완료 후 로그인 페이지 표시
    return <Login />;
  }

  return (
    <ToastProvider>
      <div style={styles.app}>
        {/* 헤더 */}
        <header style={styles.header}>
  <div style={styles.headerContent}>
    <div style={styles.logoWrapper}>
      <img
        src="/logo192.png"
        alt="출퇴근 계단 운동 앱 로고"
        style={styles.logoIcon}
      />
      <span style={styles.logoText}>출퇴근 계단 운동 앱</span>
    </div>

    <div style={styles.headerButtons}>
      <button
        onClick={() => setShowSettingsMenu(!showSettingsMenu)}
        style={styles.settingsButton}
      >
        ≡
      </button>
    </div>
  </div>

          {/* 설정 메뉴 */}
          {showSettingsMenu && (
            <div style={styles.settingsMenu}>
              <button onClick={() => { setCurrentPage('profile'); setShowSettingsMenu(false); }}>
                ♠ 프로필
              </button>
              <button onClick={() => { setCurrentPage('friends'); setShowSettingsMenu(false); }}>
                ♦ 친구
              </button>
              <button onClick={() => { setCurrentPage('groups'); setShowSettingsMenu(false); }}>
                ♥ 그룹
              </button>
              <button onClick={() => { setCurrentPage('feedback'); setShowSettingsMenu(false); }}>
                ♣ 불편사항 접수
              </button>
              {isAdmin && (
                <button onClick={() => { setCurrentPage('admin'); setShowSettingsMenu(false); }}>
                   관리자 패널
                </button>
              )}
            </div>
          )}
        </header>

        {/* 네비게이션 바 (신규 사용자는 숨김) */}
{!isNewUser && (
  <nav style={styles.nav}>
    <button 
      onClick={() => setCurrentPage('home')}
      style={{
        ...styles.navButton,
        ...(currentPage === 'home' ? styles.navButtonActive : {})
      }}
    >
      [ HOME ]
    </button>

    <button 
      onClick={() => setCurrentPage('ranking')}
      style={{
        ...styles.navButton,
        ...(currentPage === 'ranking' ? styles.navButtonActive : {})
      }}
    >
      [ RANKING ]
    </button>

    <button 
      onClick={() => setCurrentPage('tutorial')}
      style={{
        ...styles.navButton,
        ...(currentPage === 'tutorial' ? styles.navButtonActive : {})
      }}
    >
      [ TUTORIAL ]
    </button>
  </nav>
)}


        {/* 메인 컨텐츠 */}
        <main style={styles.main}>
          {currentPage === 'home' && <Home onNavigateToRecord={() => setCurrentPage('record')} />}
          {currentPage === 'record' && <StairRecord />}
          {currentPage === 'ranking' && <Ranking />}
          {currentPage === 'friends' && <Friends />}
          {currentPage === 'groups' && <Groups />}
          {currentPage === 'profile' && <Profile onComplete={handleProfileComplete} />}
          {currentPage === 'feedback' && <Feedback />}
          {currentPage === 'admin' && isAdmin && <AdminPanel userEmail={user.email} />}
          {currentPage === 'tutorial' && <Tutorial />}
        </main>

        {/* 푸터 */}
        <footer style={styles.footer}>
          <div style={styles.footerContent}>
            <div style={styles.slogan}>No, hardday.</div>
            <div style={styles.contact}>
              <div style={styles.contactItem}>
                Email: <a href="mailto:moveoftoday.info@gmail.com" style={styles.link}>moveoftoday.info@gmail.com</a>
              </div>
              <div style={styles.contactItem}>
                Instagram: <a href="https://www.instagram.com/moveoftoday/" target="_blank" rel="noopener noreferrer" style={styles.link}>@moveoftoday</a>
              </div>
            </div>
            <div style={styles.copyright}>
              © 2024 Move of Today. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </ToastProvider>
  );
}

const styles = {
  app: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    fontSize: '14px',
    lineHeight: 1.4
  },

  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#ffffff'
  },

  loadingText: {
    fontSize: '16px',
    color: '#555'
  },

  // 헤더
  header: {
    backgroundColor: '#ffffff',
    color: '#000000',
    padding: '10px 16px',
    borderBottom: '1px solid #ddd',
    position: 'sticky',
    top: 0,
    zIndex: 100
  },

  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto'
  },

  logoWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  logoIcon: {
    width: '22px',
    height: '22px',
    objectFit: 'contain'
  },

  logoText: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold'
  },

  logo: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600'
  },

  headerButtons: {
    display: 'flex',
    gap: '8px'
  },

  settingsButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    padding: 0
  },

  settingsMenu: {
    position: 'absolute',
    top: '42px',
    right: '16px',
    backgroundColor: '#ffffff',
    border: '1px solid #ddd',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '160px',
    zIndex: 1000
  },

  logoutButton: {
    color: '#c62828'
  },

  nav: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #ddd',
    padding: '6px 16px',
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  },

  navButton: {
    padding: '6px 10px',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '13px',
    cursor: 'pointer',
    color: '#555'
  },

  navButtonActive: {
    textDecoration: 'underline',
    fontWeight: '600',
    color: '#000'
  },

  main: {
    flex: 1,
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    padding: '16px'
  },

  footer: {
    backgroundColor: '#808080',
    color: '#ffffff',
    borderTop: '1px solid #000',
    padding: '12px 16px',
    marginTop: 'auto'
  },

  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    textAlign: 'center',
    fontSize: '12px'
  },

  slogan: {
    fontSize: '14px',
    fontWeight: '700',
    marginBottom: '6px',
    letterSpacing: '0.5px'
  },

  contact: {
    marginBottom: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },

  contactItem: {
    fontSize: '11px',
    lineHeight: 1.4
  },

  link: {
    color: '#ffffff',
    textDecoration: 'underline'
  },

  copyright: {
    fontSize: '10px',
    color: '#dddddd',
    marginTop: '4px'
  }
};


export default App;
